/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { Button } from '@mui/material';
import DefaultModal from './DefaultModal';
import { useAutopayContext } from '../../contexts/AutopayContext';
import Styles from '../../css/AutopayModal.module.css';
import dayjs from 'dayjs';
import { numericFormatter } from 'react-number-format';
import { useViewingContext } from '../../contexts/ViewingContext';

type autopayModalProps = {
  content: string;
};

const AutopayModal = (props: autopayModalProps) => {
  const autopayContext = useAutopayContext();
  const { isServicerView } = useViewingContext();

  if (props.content === 'confirm') {
    return (
      <DefaultModal
        open={autopayContext.autopayModalOpen}
        onClose={() => autopayContext.setAutopayModalOpen(false)}
        title={<h1>Confirm Autopay Details</h1>}
        content={
          <div className={Styles.gridContainer}>
            {isServicerView && (
              <>
                <p className={Styles.label}>Loan EFT Flag: </p>
                <p>
                  {
                    autopayContext.autopayStatusQidInfo.find(
                      (option) => option.keyword === autopayContext.autopayInfo.autoPayStatusKeyword,
                    )?.description
                  }
                </p>
              </>
            )}
            <p className={Styles.label}>Transit Routing No: </p>
            <p>{autopayContext.autopayInfo.eftRoutingNo}</p>
            <p className={Styles.label}>Bank Name: </p>
            <p>{autopayContext.autopayInfo.eftBankName}</p>
            <p className={Styles.label}>Bank Account No: </p>
            <p>{autopayContext.autopayInfo.eftBankAccountNo}</p>
            <p className={Styles.label}>Bank Account Type: </p>
            <p>
              {
                autopayContext.eftBankAcctTypeQidInfo.find(
                  (option) => option.keyword === autopayContext.autopayInfo.eftBankAccountTypeKeyword,
                )?.description
              }
            </p>
            <p className={Styles.label}>Start Date: </p>
            <p>{dayjs.unix(autopayContext.autopayInfo?.startDate).format('MM/DD/YYYY')}</p>
            {isServicerView && (
              <>
                <p className={Styles.label}>Last Transmit Date: </p>
                <p>{dayjs.unix(autopayContext.loanInfo?.lpi).format('MM/DD/YYYY')}</p>
              </>
            )}
            <p className={Styles.label}>Day of Month to Draft: </p>
            <p>{autopayContext.autopayInfo.settleDay}</p>
            {isServicerView && (
              <>
                <p className={Styles.label}>EFT Payment: </p>
                <p>
                  {numericFormatter(autopayContext.payment.toString(), {
                    thousandSeparator: true,
                    prefix: '$',
                    decimalScale: 2,
                    fixedDecimalScale: true,
                  })}
                </p>
              </>
            )}
            <p className={Styles.label}>Additional Principal: </p>
            <p>
              {numericFormatter(autopayContext.autopayInfo.additionalPrincipal.toString(), {
                thousandSeparator: true,
                prefix: '$',
                decimalScale: 2,
                fixedDecimalScale: true,
              })}
            </p>
            <p className={Styles.label}>Additional Escrow: </p>
            <p>
              {numericFormatter(autopayContext.autopayInfo.additionalEscrow.toString(), {
                thousandSeparator: true,
                prefix: '$',
                decimalScale: 2,
                fixedDecimalScale: true,
              })}
            </p>
            {isServicerView && (
              <>
                <p className={Styles.label}>Total EFT Payment: </p>
                <p>
                  {numericFormatter(
                    (
                      autopayContext.payment +
                      autopayContext.autopayInfo.additionalPrincipal +
                      autopayContext.autopayInfo.additionalEscrow
                    ).toString(),
                    {
                      thousandSeparator: true,
                      prefix: '$',
                      decimalScale: 2,
                      fixedDecimalScale: true,
                    },
                  )}
                </p>
                <p className={Styles.label}>Method Of Authorization: </p>
                <p>
                  {
                    autopayContext.authMethodQidInfo.find(
                      (option) => option.keyword === autopayContext.autopayInfo.authorizationMethodKeyword,
                    )?.description
                  }
                </p>
                <p className={Styles.label}>Authorization Date: </p>
                <p>{dayjs.unix(autopayContext.autopayInfo?.authorizationDate).format('MM/DD/YYYY')}</p>
              </>
            )}
          </div>
        }
        footer={
          <div className={Styles.footer}>
            <Button
              variant='outlined'
              onClick={() => autopayContext.setAutopayModalOpen(false)}
              className={Styles.cancelButton}
            >
              Cancel
            </Button>
            <Button variant='contained' onClick={(e) => autopayContext.handleSubmit(e)} className={Styles.button}>
              Confirm
            </Button>
          </div>
        }
      />
    );
  }

  if (props.content === 'removeBankDetails') {
    return (
      <DefaultModal
        open={autopayContext.autopayModalOpen}
        onClose={() => autopayContext.setAutopayModalOpen(false)}
        title={<h1>Remove Bank Details?</h1>}
        content={
          <div className={Styles.stopCodeModalContainer}>
            <div className={Styles.stopCodeText}>
              Are you sure you want to clear your bank details from the loan? This will clear routing number, bank
              account name, bank account number, and bank account type.
            </div>
          </div>
        }
        footer={
          <div className={Styles.footer}>
            <Button
              variant='outlined'
              onClick={() => autopayContext.setAutopayModalOpen(false)}
              className={Styles.cancelButton}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={() => autopayContext.handleRemoveBankDetails()}
              className={Styles.button}
            >
              Confirm
            </Button>
          </div>
        }
      />
    );
  }

  if (props.content === 'stopCodeST04') {
    return isServicerView ? (
      <DefaultModal
        open={autopayContext.autopayModalOpen}
        onClose={() => autopayContext.setAutopayModalOpen(false)}
        title={<h1>Stop Code ST04</h1>}
        content={
          <div className={Styles.stopCodeModalContainer}>
            <div className={Styles.stopCodeText}>Unable to set up Autopay due to ST04</div>
          </div>
        }
      />
    ) : (
      <DefaultModal
        open={autopayContext.autopayModalOpen}
        onClose={() => autopayContext.setAutopayModalOpen(false)}
        title={<h1>Cannot Accept Payment</h1>}
        content={
          <div className={Styles.stopCodeModalContainer}>
            <div className={Styles.stopCodeText}>
              Autopay cannot be set up at this time. Please contact CustomerService@swmc.com for assistance.
            </div>
          </div>
        }
      />
    );
  }
};

export default AutopayModal;
